import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Star, Settings, Download, FileText, Building2,
  CheckCircle, Clock, AlertCircle, RefreshCw, Plus, Edit2,
  BarChart2, Award, X, Save, Zap
} from 'lucide-react';
import api from '../services/api';

// ─── Константы ───────────────────────────────────────────────────────────────

const MONTH_VALUES = [1,2,3,4,5,6,7,8,9,10,11,12];

const GRADE_COLORS = {
  A: { bg: '#d4edda', text: '#155724', border: '#c3e6cb' },
  B: { bg: '#fff3cd', text: '#856404', border: '#ffeeba' },
  C: { bg: '#ffe8cc', text: '#7d4800', border: '#ffd5a8' },
  D: { bg: '#f8d7da', text: '#721c24', border: '#f5c6cb' },
};

const STATUS_COLORS = {
  draft:     { color: '#6c757d', icon: Edit2 },
  submitted: { color: '#0d6efd', icon: Clock },
  approved:  { color: '#198754', icon: CheckCircle },
  rejected:  { color: '#dc3545', icon: AlertCircle },
};


// ─── Вспомогательные компоненты ───────────────────────────────────────────────

const GradeBadge = ({ grade, percentage }) => {
  const cfg = GRADE_COLORS[grade] || { bg: '#f8f9fa', text: '#495057', border: '#dee2e6' };
  return (
    <span style={{
      background: cfg.bg, color: cfg.text, border: `1px solid ${cfg.border}`,
      borderRadius: 6, padding: '2px 10px', fontWeight: 700, fontSize: 13,
    }}>
      {grade} {percentage !== undefined && `(${percentage?.toFixed(0)}%)`}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const { t } = useTranslation('kpi');
  const cfg = STATUS_COLORS[status] || { color: '#6c757d', icon: Clock };
  const Icon = cfg.icon || Clock;
  const label = STATUS_COLORS[status] ? t(`status.${status}`) : status;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: cfg.color + '18', color: cfg.color,
      border: `1px solid ${cfg.color}40`,
      borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 600,
    }}>
      <Icon size={12} />{label}
    </span>
  );
};

const ScoreBar = ({ score, maxScore, color = '#0d6efd' }) => {
  const pct = maxScore > 0 ? Math.min((score / maxScore) * 100, 100) : 0;
  let barColor = color;
  if (pct >= 90) barColor = '#198754';
  else if (pct >= 75) barColor = '#ffc107';
  else if (pct >= 60) barColor = '#fd7e14';
  else barColor = '#dc3545';

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
        <span style={{ fontWeight: 600 }}>{score} / {maxScore}</span>
        <span style={{ color: barColor, fontWeight: 700 }}>{pct.toFixed(0)}%</span>
      </div>
      <div style={{ background: '#e9ecef', borderRadius: 4, height: 8, overflow: 'hidden' }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: barColor, borderRadius: 4,
          transition: 'width 0.4s ease',
        }} />
      </div>
    </div>
  );
};

// ─── Модал оценки сотрудника ──────────────────────────────────────────────────

const EvaluationModal = ({ employee, record, template, month, year, onClose, onSave }) => {
  const { t } = useTranslation('kpi');
  const isApproved = record?.status === 'approved';

  const [form, setForm] = useState({
    criteria_evaluations: [],
  });
  const [saving, setSaving] = useState(false);

  // Инициализируем v2 evaluations из шаблона (план из шаблона, факт из записи)
  const buildV2Evals = (existingEvals) => {
    if (!template?.criteria) return existingEvals || [];
    const evals = [];
    const existMap = {};
    (existingEvals || []).forEach(e => {
      if (e.criterion_number) existMap[e.criterion_number] = e;
    });
    (template.criteria || []).forEach(group => {
      (group.subcriteria || []).forEach(sc => {
        const num = sc.number || '';
        const existing = existMap[num];
        // План берём из записи (если есть), иначе из шаблона
        const planVal = existing?.plan_value ?? sc.plan_value ?? 0;
        evals.push({
          group_number: group.group_number,
          criterion_number: num,
          criterion_name: sc.name || '',
          weight: sc.weight || 0,
          plan_value: planVal,
          actual_value: existing?.actual_value ?? 0,
          execution_pct: existing?.execution_pct ?? 0,
          score: existing?.score ?? 0,
          comment: existing?.comment ?? '',
        });
      });
    });
    return evals;
  };

  useEffect(() => {
    if (record) {
      const recEvals = record.criteria_evaluations || [];
      setForm({
        criteria_evaluations: buildV2Evals(recEvals),
      });
    } else {
      setForm({ criteria_evaluations: buildV2Evals([]) });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record, template]);

  const criteria = template?.criteria || [];

  // ── хелперы план/факт ──
  const getV2Eval = (criterionNumber) =>
    (form.criteria_evaluations || []).find(e => e.criterion_number === criterionNumber);

  // Получаем group_weight и сумму весов подкритериев для нормализации
  const getGroupInfo = (groupNumber) => {
    const group = (criteria || []).find(g => g.group_number === groupNumber);
    if (!group) return { groupWeight: 0, subWeightsSum: 0 };
    const subWeightsSum = (group.subcriteria || []).reduce((s, sc) => s + (sc.weight || 0), 0);
    return { groupWeight: group.group_weight || 0, subWeightsSum };
  };

  const updateV2Eval = (criterionNumber, field, value) => {
    setForm(prev => {
      const evals = [...(prev.criteria_evaluations || [])];
      const idx = evals.findIndex(e => e.criterion_number === criterionNumber);
      if (idx >= 0) {
        const updated = { ...evals[idx], [field]: value };
        // Авторасчёт execution_pct и score
        if (field === 'plan_value' || field === 'actual_value') {
          const plan = field === 'plan_value' ? Number(value) : Number(updated.plan_value || 0);
          const actual = field === 'actual_value' ? Number(value) : Number(updated.actual_value || 0);
          updated.execution_pct = plan > 0 ? Math.round(actual / plan * 100 * 10) / 10 : 0;
          // Нормализация: score пропорционален group_weight, не weight подкритерия
          // При 100% исполнения ВСЕХ подкритериев группы, сумма = group_weight
          const { groupWeight, subWeightsSum } = getGroupInfo(updated.group_number);
          const normalizedWeight = subWeightsSum > 0 ? (updated.weight / subWeightsSum) * groupWeight : updated.weight;
          updated.score = Math.round(normalizedWeight * Math.min(updated.execution_pct, 100) / 100 * 10) / 10;
        }
        if (field === 'score') {
          updated.score = Number(value);
        }
        evals[idx] = updated;
      }
      return { ...prev, criteria_evaluations: evals };
    });
  };

  // Итоговый балл
  const criteriaTotal = (form.criteria_evaluations || []).reduce((s, e) => s + (Number(e.score) || 0), 0);
  const total = Math.round(criteriaTotal);
  const maxPossible = template?.total_max_score || 100;

  // Шкала из шаблона (баллы)
  const getGrade = () => {
    const scale = template?.grading_scale || {};
    const aMin = scale?.A?.min ?? 100;
    const bMin = scale?.B?.min ?? 85;
    const cMin = scale?.C?.min ?? 70;
    if (total >= aMin) return 'A';
    if (total >= bMin) return 'B';
    if (total >= cMin) return 'C';
    return 'D';
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const pm = record?.period_month ?? month;
      const py = record?.period_year ?? year;

      const createPayload = {
        ...form,
        employee: employee.id,
        department: employee.department,
        period_month: pm,
        period_year: py,
        max_possible_score: maxPossible,
      };

      const updatePayload = {
        ...form,
        max_possible_score: maxPossible,
      };

      if (record?.id) {
        await api.updateKPIRecord(record.id, updatePayload);
      } else {
        try {
          await api.createKPIRecord(createPayload);
        } catch (createErr) {
          if (createErr.response?.status === 400) {
            const existing = await api.getKPIRecords({
              employee: employee.id, month: pm, year: py, page_size: 10,
            });
            const existingList = existing.data?.results || existing.data || [];
            if (existingList.length > 0) {
              await api.updateKPIRecord(existingList[0].id, updatePayload);
            } else {
              throw createErr;
            }
          } else {
            throw createErr;
          }
        }
      }
      onSave();
      onClose();
    } catch (e) {
      alert(t('evalModal.saveError') + ' ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  // ── v2: рендер группированных критериев с план/факт ──
  const renderV2Criteria = () => {
    return (criteria || []).map((group, gi) => (
      <div key={gi} style={{ marginBottom: 16 }}>
        {/* Заголовок группы */}
        <div style={{
          background: '#e3f2fd', borderRadius: 8, padding: '10px 14px', marginBottom: 8,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: '#1565c0' }}>
            {group.group_number}. {group.group_name}
          </span>
          <span style={{ fontWeight: 600, fontSize: 13, color: '#1565c0' }}>
            {group.group_weight}%
          </span>
        </div>
        {/* Подкритерии */}
        {(group.subcriteria || []).map((sc, si) => {
          const ev = getV2Eval(sc.number);
          return (
            <div key={si} style={{
              background: '#fff', border: '1px solid #e9ecef', borderRadius: 8,
              padding: 12, marginBottom: 8, marginLeft: 16,
            }}>
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>{sc.number}. {sc.name}</span>
                <span style={{ marginLeft: 8, fontSize: 12, color: '#6c757d' }}>
                  (max: {sc.weight} ball)
                </span>
              </div>
              {sc.scoring_rule && (
                <p style={{ margin: '0 0 8px', fontSize: 11, color: '#6c757d', lineHeight: 1.4 }}>
                  {sc.scoring_rule.substring(0, 200)}{sc.scoring_rule.length > 200 ? '...' : ''}
                </p>
              )}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <label style={{ fontSize: 12, color: '#495057' }}>
                  Reja (plan):
                  <input
                    type="number" min={0}
                    value={ev?.plan_value || ''}
                    onChange={e => {
                      const val = e.target.value;
                      updateV2Eval(sc.number, 'plan_value', val === '' ? 0 : Number(val));
                    }}
                    disabled={isApproved}
                    style={{
                      marginLeft: 6, width: 70, padding: '4px 8px', borderRadius: 6,
                      border: '1px solid #dee2e6', fontSize: 13, textAlign: 'center',
                    }}
                  />
                </label>
                <label style={{ fontSize: 12, color: '#495057' }}>
                  Fakt (actual):
                  <input
                    type="number" min={0}
                    value={ev?.actual_value || ''}
                    onChange={e => {
                      const val = e.target.value;
                      updateV2Eval(sc.number, 'actual_value', val === '' ? 0 : Number(val));
                    }}
                    disabled={isApproved}
                    style={{
                      marginLeft: 6, width: 70, padding: '4px 8px', borderRadius: 6,
                      border: '1px solid #dee2e6', fontSize: 13, textAlign: 'center',
                    }}
                  />
                </label>
                <span style={{
                  fontSize: 12, color: (ev?.execution_pct || 0) >= 100 ? '#198754' : '#dc3545',
                  fontWeight: 700,
                }}>
                  {(ev?.execution_pct || 0).toFixed(1)}%
                </span>
                <span style={{ fontSize: 12, fontWeight: 700 }}>
                  → {(ev?.score || 0).toFixed(1)} / {sc.weight} ball
                </span>
              </div>
            </div>
          );
        })}
      </div>
    ));
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, width: '100%', maxWidth: 750,
        maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        {/* Заголовок */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #e9ecef',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: '#f8f9fa', borderRadius: '12px 12px 0 0',
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
              {t('evalModal.title')}
            </h3>
            <p style={{ margin: '4px 0 0', color: '#6c757d', fontSize: 14 }}>
              {employee?.last_name} {employee?.first_name} — {record?.period_display}
            </p>
            <span style={{
              fontSize: 11, background: '#e8f5e9', color: '#2e7d32',
              padding: '2px 8px', borderRadius: 4, marginTop: 4, display: 'inline-block',
            }}>
              Reja / Fakt formati
            </span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={20} color="#6c757d" />
          </button>
        </div>

        <div style={{ padding: 24 }}>
          {/* Прогресс-бар итога */}
          <div style={{
            background: '#f8f9fa', borderRadius: 10, padding: 16, marginBottom: 20,
            border: '1px solid #e9ecef',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontWeight: 600, fontSize: 15 }}>{t('evalModal.totalScore')}</span>
              <GradeBadge grade={getGrade()} />
            </div>
            <ScoreBar score={total} maxScore={maxPossible} />
            <div style={{ fontSize: 12, color: '#6c757d', marginTop: 6 }}>
              {total} ball / {maxPossible} max — {getGrade()}
            </div>
          </div>

          {/* Группы с план/факт */}
          <div style={{ marginBottom: 20 }}>
            {renderV2Criteria()}
          </div>

          {/* Кнопки */}
          {isApproved ? (
            <div style={{
              background: '#d1e7dd', border: '1px solid #a3cfbb', borderRadius: 8,
              padding: '10px 16px', color: '#0f5132', fontSize: 13, textAlign: 'center',
            }}>
              {t('evalModal.approvedLocked')}
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={{
                padding: '9px 20px', borderRadius: 8, border: '1px solid #dee2e6',
                background: '#fff', cursor: 'pointer', fontSize: 14,
              }}>
                {t('evalModal.cancel')}
              </button>
              <button onClick={handleSave} disabled={saving} style={{
                padding: '9px 20px', borderRadius: 8, border: 'none',
                background: saving ? '#6c757d' : '#0d6efd', color: '#fff',
                cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <Save size={15} />
                {saving ? t('evalModal.saving') : t('evalModal.save')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Модал настройки шаблона ──────────────────────────────────────────────────

const MONTHS_UZ_LIST = [
  { v: 1, uz: 'yanvar' }, { v: 2, uz: 'fevral' }, { v: 3, uz: 'mart' },
  { v: 4, uz: 'aprel' }, { v: 5, uz: 'may' }, { v: 6, uz: 'iyun' },
  { v: 7, uz: 'iyul' }, { v: 8, uz: 'avgust' }, { v: 9, uz: 'sentabr' },
  { v: 10, uz: 'oktabr' }, { v: 11, uz: 'noyabr' }, { v: 12, uz: 'dekabr' },
];

const TemplateModal = ({ template, department: deptProp, onClose, onSave }) => {
  const { t } = useTranslation('kpi');
  const [groups, setGroups] = useState(template?.criteria?.length ? (template.criteria) : [
    { group_number: 1, group_name: "Asosiy vazifalar bo'yicha", group_weight: 50, subcriteria: [] },
    { group_number: 2, group_name: "Sifat ko'rsatkichlari bo'yicha", group_weight: 35, subcriteria: [] },
    { group_number: 3, group_name: "Davomat ko'rsatkichlari bo'yicha", group_weight: 15, subcriteria: [] },
  ]);

  const [docTitle, setDocTitle] = useState(template?.document_title || '');
  const [orderYear,   setOrderYear]   = useState(template?.order_year   || 2026);
  const [orderMonth,  setOrderMonth]  = useState(template?.order_month  || 8);
  const [orderDay,    setOrderDay]    = useState(template?.order_day    || '');
  const [orderNumber, setOrderNumber] = useState(template?.order_number || '');
  const [saving, setSaving] = useState(false);
  const [depts, setDepts] = useState([]);
  const [selDept, setSelDept] = useState(deptProp || '');

  useEffect(() => {
    if (!deptProp) {
      api.getDepartments?.().then(r => setDepts(r.data?.results || r.data || []));
    }
  }, [deptProp]);

  const department = deptProp || selDept;

  // ── хелперы групп ──
  const addGroup = () => {
    const nextNum = groups.length + 1;
    setGroups(prev => [...prev, { group_number: nextNum, group_name: '', group_weight: 0, subcriteria: [] }]);
  };
  const updateGroup = (gi, field, value) => {
    setGroups(prev => {
      const updated = [...prev];
      updated[gi] = { ...updated[gi], [field]: value };
      return updated;
    });
  };
  const removeGroup = (gi) => setGroups(prev => prev.filter((_, i) => i !== gi));
  const addSubcriterion = (gi) => {
    setGroups(prev => {
      const updated = [...prev];
      const grp = { ...updated[gi] };
      const nextNum = `${grp.group_number}.${(grp.subcriteria || []).length + 1}`;
      grp.subcriteria = [...(grp.subcriteria || []), { number: nextNum, name: '', weight: 0, scoring_rule: '' }];
      updated[gi] = grp;
      return updated;
    });
  };
  const updateSubcriterion = (gi, si, field, value) => {
    setGroups(prev => {
      const updated = [...prev];
      const grp = { ...updated[gi] };
      const subs = [...grp.subcriteria];
      subs[si] = { ...subs[si], [field]: value };
      grp.subcriteria = subs;
      updated[gi] = grp;
      return updated;
    });
  };
  const removeSubcriterion = (gi, si) => {
    setGroups(prev => {
      const updated = [...prev];
      const grp = { ...updated[gi] };
      grp.subcriteria = grp.subcriteria.filter((_, i) => i !== si);
      updated[gi] = grp;
      return updated;
    });
  };

  const totalWeight = groups.reduce((s, g) => s + (g.subcriteria || []).reduce((ss, sc) => ss + (Number(sc.weight) || 0), 0), 0);

  const handleSave = async () => {
    if (!department) { alert(t('templateModal.deptRequired')); return; }
    setSaving(true);
    try {
      const payload = {
        criteria: groups,
        criteria_version: 2,
        document_title: docTitle,
        order_year: orderYear, order_month: orderMonth,
        order_day: orderDay, order_number: orderNumber,
      };
      if (!template?.id) payload.department = department;
      if (template?.id) {
        await api.updateKPITemplate(template.id, payload);
      } else {
        await api.createKPITemplate(payload);
      }
      onSave();
      onClose();
    } catch (e) {
      alert(t('templateModal.saveError') + ' ' + JSON.stringify(e.response?.data || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, width: '100%', maxWidth: 800,
        maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #e9ecef',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: '#f8f9fa', borderRadius: '12px 12px 0 0',
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 17 }}>{t('templateModal.title')}</h3>
            {template?.id && (
              <p style={{ margin: '3px 0 0', fontSize: 12, color: '#6c757d' }}>
                {t('templateModal.editSubtitle', { id: template.department })}
              </p>
            )}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={20} color="#6c757d" />
          </button>
        </div>

        <div style={{ padding: 24 }}>
          {/* Выбор отдела */}
          {!deptProp && depts.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                {t('templateModal.deptLabel')}
              </label>
              <select value={selDept} onChange={e => setSelDept(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 7, border: '1px solid #dee2e6', fontSize: 14 }}>
                <option value="">{t('templateModal.deptPlaceholder')}</option>
                {depts.map(d => <option key={d.id} value={d.id}>{d.name_ru || d.name}</option>)}
              </select>
            </div>
          )}

          {/* Заголовок документа */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              {t('templateModal.docTitleLabel')}
            </label>
            <textarea value={docTitle} onChange={e => setDocTitle(e.target.value)}
              placeholder={t('templateModal.docTitlePlaceholder')} rows={2}
              style={{ width: '100%', borderRadius: 7, border: '1px solid #dee2e6', padding: '8px 12px', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
            />
          </div>

          {/* Группы с подкритериями */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
                  Guruhlar ({groups.length}) — jami: {totalWeight} ball
                </h4>
                <button onClick={addGroup} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '6px 14px', borderRadius: 7, background: '#198754', color: '#fff',
                  border: 'none', cursor: 'pointer', fontSize: 13,
                }}>
                  <Plus size={14} /> Guruh qo'shish
                </button>
              </div>

              {groups.map((grp, gi) => (
                <div key={gi} style={{
                  border: '2px solid #e3f2fd', borderRadius: 10, marginBottom: 14,
                  background: '#fafcff',
                }}>
                  {/* Заголовок группы */}
                  <div style={{
                    padding: '10px 14px', background: '#e3f2fd', borderRadius: '8px 8px 0 0',
                    display: 'flex', gap: 8, alignItems: 'center',
                  }}>
                    <span style={{
                      background: '#1565c0', color: '#fff', borderRadius: '50%',
                      width: 24, height: 24, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0,
                    }}>{grp.group_number}</span>
                    <input placeholder="Guruh nomi" value={grp.group_name}
                      onChange={e => updateGroup(gi, 'group_name', e.target.value)}
                      style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid #90caf9', fontSize: 13 }} />
                    <label style={{ fontSize: 12, color: '#1565c0', whiteSpace: 'nowrap' }}>
                      Ulushi:
                      <input type="number" min={0} max={100} value={grp.group_weight}
                        onChange={e => updateGroup(gi, 'group_weight', Number(e.target.value))}
                        style={{ marginLeft: 4, width: 50, padding: '4px 6px', borderRadius: 5, border: '1px solid #90caf9', fontSize: 12, textAlign: 'center' }} />
                      %
                    </label>
                    <button onClick={() => removeGroup(gi)} style={{
                      background: '#fff0f0', border: '1px solid #f5c6cb', borderRadius: 6,
                      padding: '5px 8px', cursor: 'pointer',
                    }}>
                      <X size={14} color="#dc3545" />
                    </button>
                  </div>
                  {/* Подкритерии */}
                  <div style={{ padding: '10px 14px' }}>
                    {(grp.subcriteria || []).map((sc, si) => (
                      <div key={si} style={{
                        border: '1px solid #e9ecef', borderRadius: 6, padding: 10, marginBottom: 8,
                        background: '#fff',
                      }}>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#1565c0', minWidth: 30 }}>
                            {sc.number}
                          </span>
                          <input placeholder="Ko'rsatkich nomi" value={sc.name}
                            onChange={e => updateSubcriterion(gi, si, 'name', e.target.value)}
                            style={{ flex: 1, padding: '5px 8px', borderRadius: 5, border: '1px solid #dee2e6', fontSize: 12 }} />
                          <label style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                            Ball:
                            <input type="number" min={0} max={100} value={sc.weight}
                              onChange={e => updateSubcriterion(gi, si, 'weight', Number(e.target.value))}
                              style={{ marginLeft: 4, width: 50, padding: '3px 6px', borderRadius: 4, border: '1px solid #dee2e6', fontSize: 12, textAlign: 'center' }} />
                          </label>
                          <button onClick={() => removeSubcriterion(gi, si)} style={{
                            background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                          }}>
                            <X size={12} color="#dc3545" />
                          </button>
                        </div>
                        <textarea placeholder="Baholash qoidasi (izoh)" value={sc.scoring_rule || ''}
                          onChange={e => updateSubcriterion(gi, si, 'scoring_rule', e.target.value)}
                          rows={2}
                          style={{
                            width: '100%', borderRadius: 5, border: '1px solid #e9ecef',
                            padding: '6px 8px', fontSize: 11, resize: 'vertical', boxSizing: 'border-box',
                          }} />
                      </div>
                    ))}
                    <button onClick={() => addSubcriterion(gi)} style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      padding: '5px 12px', borderRadius: 6, border: '1px dashed #90caf9',
                      background: '#f5f9ff', cursor: 'pointer', fontSize: 12, color: '#1565c0',
                    }}>
                      <Plus size={12} /> Ko'rsatkich qo'shish
                    </button>
                  </div>
                </div>
              ))}
            </div>}

          {/* Реквизиты приказа */}
          <div style={{ background: '#fff9e6', border: '1px solid #ffe58f', borderRadius: 8, padding: 14, marginBottom: 20 }}>
            <h4 style={{ margin: '0 0 10px', fontSize: 14, color: '#7d4800' }}>{t('templateModal.orderTitle')}</h4>
            <div style={{
              background: '#fff', border: '1px dashed #ffd666', borderRadius: 6,
              padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#495057',
              lineHeight: 1.7, textAlign: 'right', fontStyle: 'italic',
            }}>
              "Farmatsevtika mahsulotlari xavfsizligi<br/>
              markazi" davlat muassasasi direktorining<br/>
              <strong>{orderYear}</strong>-yil "{orderDay || '___'}" <strong>{MONTHS_UZ_LIST.find(m => m.v === orderMonth)?.uz || 'avgust'}dagi</strong> <strong>{orderNumber || '____'}</strong> -sonli buyrug'iga<br/>
              <strong>2-ilova</strong>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <label style={{ fontSize: 13, color: '#7d4800' }}>
                {t('templateModal.orderYear')}
                <input type="number" value={orderYear} min={2020} max={2099}
                  onChange={e => setOrderYear(Number(e.target.value))}
                  style={{ marginLeft: 6, width: 80, padding: '5px 8px', borderRadius: 6, border: '1px solid #ffe58f', fontSize: 13 }} />
              </label>
              <label style={{ fontSize: 13, color: '#7d4800' }}>
                {t('templateModal.orderMonth')}
                <select value={orderMonth} onChange={e => setOrderMonth(Number(e.target.value))}
                  style={{ marginLeft: 6, padding: '5px 8px', borderRadius: 6, border: '1px solid #ffe58f', fontSize: 13 }}>
                  {MONTHS_UZ_LIST.map(m => <option key={m.v} value={m.v}>{m.uz}</option>)}
                </select>
              </label>
              <label style={{ fontSize: 13, color: '#7d4800' }}>
                {t('templateModal.orderDay')}
                <input type="text" value={orderDay} placeholder="___"
                  onChange={e => setOrderDay(e.target.value)}
                  style={{ marginLeft: 6, width: 60, padding: '5px 8px', borderRadius: 6, border: '1px solid #ffe58f', fontSize: 13 }} />
              </label>
              <label style={{ fontSize: 13, color: '#7d4800' }}>
                {t('templateModal.orderNumber')}
                <input type="text" value={orderNumber} placeholder="____"
                  onChange={e => setOrderNumber(e.target.value)}
                  style={{ marginLeft: 6, width: 80, padding: '5px 8px', borderRadius: 6, border: '1px solid #ffe58f', fontSize: 13 }} />
              </label>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={{
              padding: '9px 20px', borderRadius: 8, border: '1px solid #dee2e6',
              background: '#fff', cursor: 'pointer', fontSize: 14,
            }}>{t('templateModal.cancel')}</button>
            <button onClick={handleSave} disabled={saving} style={{
              padding: '9px 20px', borderRadius: 8, border: 'none',
              background: '#198754', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <Save size={15} />{saving ? t('templateModal.saving') : t('templateModal.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Главная страница ─────────────────────────────────────────────────────────

export default function EmployeeKPIPage() {
  const { t } = useTranslation('kpi');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const now = new Date();

  const [activeTab, setActiveTab] = useState('evaluations');
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [selectedDept, setSelectedDept] = useState(user.department_id || '');
  const [departments, setDepartments] = useState([]);
  const [records, setRecords] = useState([]);
  const [template, setTemplate] = useState(null);
  const [deptPlan, setDeptPlan] = useState(null);
  const [deptPlanSaving, setDeptPlanSaving] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [evalModal, setEvalModal] = useState(null); // { employee, record }
  const [templateModal, setTemplateModal] = useState(false);

  const isAdmin = ['admin', 'director', 'deputy_director'].includes(user.role);
  const isHead = user.role === 'department_head';

  const loadData = async (deptId) => {
    setLoading(true);
    try {
      const params = { month, year, page_size: 500 };
      if (deptId) params.department = deptId;
      const r = await api.getKPIRecords(params);
      setRecords(r.data?.results || r.data || []);

      // Загружаем сотрудников отдела
      const empParams = {};
      if (deptId) empParams.department = deptId;
      const empR = await api.getEmployees(empParams);
      setEmployees(empR.data?.results || empR.data || []);

      // Загружаем KPI-план отдела (полную запись с criteria_data)
      try {
        const planParams = { month, year };
        if (deptId) planParams.department = deptId;
        const planR = await api.getDeptKPIPlans(planParams);
        const plans = planR.data?.results || planR.data || [];
        const plan = plans[0] || null;
        // Если plan есть но criteria_data пустой — загрузить детали
        if (plan?.id && (!plan.criteria_data || plan.criteria_data.length === 0)) {
          try {
            const detailR = await api.getDeptKPIPlan(plan.id);
            setDeptPlan(detailR.data);
          } catch { setDeptPlan(plan); }
        } else {
          setDeptPlan(plan);
        }
      } catch { setDeptPlan(null); }
    } catch (e) {
      console.error('Ошибка загрузки KPI:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplate = async (deptId) => {
    try {
      const params = {};
      if (deptId) params.department = deptId;
      const r = await api.getKPITemplates(params);
      const list = r.data?.results || r.data || [];
      setTemplate(list[0] || null);
    } catch (e) {
      setTemplate(null);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      api.getDepartments?.().then(r => setDepartments(r.data?.results || r.data || []));
    }
    const deptId = selectedDept || user.department_id || '';
    loadData(deptId);
    loadTemplate(deptId);
  }, [month, year, selectedDept]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAutoCalculate = async () => {
    const deptId = selectedDept || user.department_id || '';
    if (!deptId) {
      alert(t('autoCalcAlert'));
      return;
    }
    try {
      const r = await api.bulkAutoCalculateKPI({
        month, year, department: deptId,
      });
      alert(t('autoCalcSuccess', { count: r.data.updated }));
      loadData(selectedDept || user.department_id || '');
    } catch (e) {
      alert('Ошибка авто-расчёта: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleDownloadWord = async (record) => {
    try {
      const r = await api.downloadKPIWord(record.id);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `KPI_${record.employee_name}_${month}_${year}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Ошибка скачивания Word: ' + e.message);
    }
  };

  const handleDownloadExcel = async () => {
    const deptId = selectedDept || user.department_id;
    if (!deptId) {
      alert('Выберите отдел для скачивания Excel');
      return;
    }
    try {
      const r = await api.downloadKPISummaryExcel({
        month, year, department: deptId,
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `KPI_summary_${month}_${year}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Ошибка скачивания Excel: ' + e.message);
    }
  };

  const handleApprove = async (recordId) => {
    if (!window.confirm(t('actions.approveConfirm'))) return;
    try {
      await api.approveKPIRecord(recordId);
      loadData(selectedDept || user.department_id || '');
    } catch (e) {
      alert('Ошибка: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Находим запись для сотрудника
  const getRecordForEmployee = (empId) =>
    records.find(r => r.employee === empId);

  // ── Рендер ──────────────────────────────────────────────────────────────────

  const tabs = [
    { key: 'evaluations', label: t('tabs.evaluations'), icon: Star },
    { key: 'dept_plan', label: "KPI bo'linma", icon: Building2 },
    { key: 'template', label: t('tabs.template'), icon: Settings },
    { key: 'reports', label: t('tabs.reports'), icon: BarChart2 },
  ];

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      {/* Заголовок */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
            <Award size={28} color="#0d6efd" />
            {t('title')}
          </h1>
          <p style={{ margin: '6px 0 0', color: '#6c757d', fontSize: 14 }}>
            {t('subtitle')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {(isAdmin || isHead) && (
            <button onClick={handleAutoCalculate} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '9px 16px', borderRadius: 8,
              background: '#6f42c1', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13,
            }}>
              <Zap size={15} /> {t('autoCalc')}
            </button>
          )}
          <button onClick={handleDownloadExcel} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '9px 16px', borderRadius: 8,
            background: '#198754', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13,
          }}>
            <Download size={15} /> Excel
          </button>
        </div>
      </div>

      {/* Фильтры */}
      <div style={{
        display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap',
        background: '#f8f9fa', padding: '14px 16px', borderRadius: 10,
        border: '1px solid #e9ecef',
      }}>
        <select
          value={month}
          onChange={e => setMonth(Number(e.target.value))}
          style={{ padding: '7px 12px', borderRadius: 7, border: '1px solid #dee2e6', fontSize: 14 }}
        >
          {MONTH_VALUES.map(m => <option key={m} value={m}>{t(`months.${m}`)}</option>)}
        </select>
        <select
          value={year}
          onChange={e => setYear(Number(e.target.value))}
          style={{ padding: '7px 12px', borderRadius: 7, border: '1px solid #dee2e6', fontSize: 14 }}
        >
          {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        {isAdmin && departments.length > 0 && (
          <select
            value={selectedDept}
            onChange={e => setSelectedDept(e.target.value)}
            style={{ padding: '7px 12px', borderRadius: 7, border: '1px solid #dee2e6', fontSize: 14, minWidth: 200 }}
          >
            <option value="">{t('allDepts')}</option>
            {departments.map(d => <option key={d.id} value={d.id}>{d.name_ru || d.name}</option>)}
          </select>
        )}
        <button onClick={() => loadData(selectedDept || user.department_id || '')} style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '7px 14px', borderRadius: 7,
          background: '#fff', border: '1px solid #dee2e6', cursor: 'pointer', fontSize: 13,
        }}>
          <RefreshCw size={14} /> {t('refresh')}
        </button>
      </div>

      {/* Вкладки */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: '2px solid #e9ecef' }}>
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 7,
                padding: '10px 18px', border: 'none', cursor: 'pointer',
                background: 'none', fontSize: 14, fontWeight: activeTab === tab.key ? 700 : 400,
                color: activeTab === tab.key ? '#0d6efd' : '#6c757d',
                borderBottom: activeTab === tab.key ? '2px solid #0d6efd' : '2px solid transparent',
                marginBottom: -2, transition: 'all 0.15s',
              }}
            >
              <Icon size={16} />{tab.label}
            </button>
          );
        })}
      </div>

      {/* ── Вкладка: Оценки ─── */}
      {activeTab === 'evaluations' && (
        <div>
          {!template && (
            <div style={{
              background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 8,
              padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <AlertCircle size={18} color="#856404" />
              <span style={{ fontSize: 14, color: '#856404' }}>
                {t('noTemplate')}
                {(isHead || isAdmin) && (
                  <button
                    onClick={() => { setActiveTab('template'); }}
                    style={{ marginLeft: 8, color: '#0d6efd', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                  >
                    {t('setupTemplate')}
                  </button>
                )}
              </span>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#6c757d' }}>{t('loading')}</div>
          ) : employees.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#6c757d' }}>{t('noEmployees')}</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#f8f9fa' }}>
                    <th style={thStyle}>{t('table.num')}</th>
                    <th style={{ ...thStyle, textAlign: 'left' }}>{t('table.employee')}</th>
                    {/* Столбцы по группам */}
                    {(template?.criteria || []).map((grp, gi) => (
                      <th key={gi} style={thStyle} title={grp.group_name}>
                        {grp.group_number}. {(grp.group_name || '').substring(0, 12)}
                      </th>
                    ))}
                    <th style={thStyle}>{t('table.total')}</th>
                    <th style={thStyle}>{t('table.grade')}</th>
                    <th style={thStyle}>{t('table.status')}</th>
                    <th style={thStyle}>{t('table.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((emp, idx) => {
                    const rec = getRecordForEmployee(emp.id);
                    return (
                      <tr key={emp.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={tdStyle}>{idx + 1}</td>
                        <td style={{ ...tdStyle, textAlign: 'left' }}>
                          <div style={{ fontWeight: 600 }}>
                            {emp.last_name} {emp.first_name}
                          </div>
                          <div style={{ fontSize: 12, color: '#6c757d' }}>{emp.position}</div>
                        </td>
                        {/* Баллы по группам */}
                        {(template?.criteria || []).map((grp, gi) => {
                          const groupScore = rec
                            ? (rec.criteria_evaluations || [])
                                .filter(e => e.group_number === grp.group_number)
                                .reduce((s, e) => s + (Number(e.score) || 0), 0)
                            : null;
                          return (
                            <td key={gi} style={tdStyle}>
                              {groupScore !== null ? (
                                <span style={{ fontWeight: 600 }}>
                                  {Math.round(groupScore * 10) / 10} / {grp.group_weight}
                                </span>
                              ) : <span style={{ color: '#adb5bd' }}>—</span>}
                            </td>
                          );
                        })}
                        <td style={tdStyle}>
                          {rec ? (
                            <ScoreBar score={rec.total_score} maxScore={rec.max_possible_score} />
                          ) : <span style={{ color: '#adb5bd' }}>—</span>}
                        </td>
                        <td style={tdStyle}>
                          {rec?.grade ? <GradeBadge grade={rec.grade} percentage={rec.percentage} /> : <span style={{ color: '#adb5bd' }}>—</span>}
                        </td>
                        <td style={tdStyle}>
                          {rec ? <StatusBadge status={rec.status} /> : (
                            <span style={{ fontSize: 12, color: '#adb5bd' }}>{t('table.notEvaluated')}</span>
                          )}
                        </td>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
                            {(isHead || isAdmin) && (
                              <button
                                onClick={async () => {
                                  if (rec?.id) {
                                    // Загружаем полную запись (с criteria_evaluations)
                                    try {
                                      const resp = await api.getKPIRecord(rec.id);
                                      setEvalModal({ employee: emp, record: resp.data });
                                    } catch { setEvalModal({ employee: emp, record: rec }); }
                                  } else {
                                    setEvalModal({ employee: emp, record: { period_month: month, period_year: year, period_display: `${t(`months.${month}`)} ${year}` } });
                                  }
                                }}
                                style={actionBtnStyle('#0d6efd')}
                                title={rec ? t('actions.editTitle') : t('actions.evaluateTitle')}
                              >
                                {rec ? <Edit2 size={13} /> : <Plus size={13} />}
                                {rec ? t('actions.edit') : t('actions.evaluate')}
                              </button>
                            )}
                            {rec && (
                              <button onClick={() => handleDownloadWord(rec)} style={actionBtnStyle('#6f42c1')} title="Скачать 2-ilova Word">
                                <FileText size={13} /> Word
                              </button>
                            )}
                            {rec && rec.status !== 'approved' && (isHead || isAdmin) && (
                              <button onClick={() => handleApprove(rec.id)} style={actionBtnStyle('#198754')} title={t('actions.approve')}>
                                <CheckCircle size={13} /> {t('actions.approve')}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Вкладка: KPI отдела ─── */}
      {activeTab === 'dept_plan' && (
        <div>
          {(() => {
            const deptId = selectedDept || user.department_id || '';
            // Группы для отдела: dept_criteria_config (свои) или fallback на шаблон сотрудника
            const groups = (deptPlan?.dept_criteria_config?.length > 0 ? deptPlan.dept_criteria_config : template?.criteria) || [];

            // Функция создания плана
            const handleCreatePlan = async () => {
              if (!deptId) { alert("Bo'linmani tanlang"); return; }
              setDeptPlanSaving(true);
              try {
                const r = await api.createDeptKPIPlan({
                  department: deptId,
                  period_month: month,
                  period_year: year,
                });
                // Инициализировать из шаблона
                if (r.data?.id) {
                  await api.initDeptKPIPlanFromTemplate(r.data.id);
                }
                loadData(deptId);
              } catch (e) {
                alert("Xato: " + (e.response?.data?.detail || e.message));
              } finally { setDeptPlanSaving(false); }
            };

            // Функция обновления критерия
            const updateDeptCriterion = (criterionNumber, field, value) => {
              setDeptPlan(prev => {
                if (!prev) return prev;
                const data = [...(prev.criteria_data || [])];
                const idx = data.findIndex(e => e.criterion_number === criterionNumber);
                if (idx >= 0) {
                  const updated = { ...data[idx], [field]: value };
                  if (field === 'plan_value' || field === 'actual_value') {
                    const plan = field === 'plan_value' ? Number(value) : Number(updated.plan_value || 0);
                    const actual = field === 'actual_value' ? Number(value) : Number(updated.actual_value || 0);
                    updated.execution_pct = plan > 0 ? Math.round(actual / plan * 100 * 10) / 10 : 0;
                    // Нормализация
                    const grp = groups.find(g => g.group_number === updated.group_number);
                    const subSum = grp ? (grp.subcriteria || []).reduce((s, sc) => s + (sc.weight || 0), 0) : 0;
                    const gw = grp ? grp.group_weight : 0;
                    const normW = subSum > 0 ? (updated.weight / subSum * gw) : updated.weight;
                    updated.score = Math.round(normW * Math.min(updated.execution_pct, 100) / 100 * 10) / 10;
                  }
                  data[idx] = updated;
                }
                return { ...prev, criteria_data: data };
              });
            };

            // Сохранение
            const handleSavePlan = async () => {
              if (!deptPlan?.id) return;
              setDeptPlanSaving(true);
              try {
                await api.updateDeptKPIPlan(deptPlan.id, {
                  criteria_data: deptPlan.criteria_data,
                  head_name: deptPlan.head_name || '',
                });
                loadData(deptId);
              } catch (e) {
                alert("Xato: " + (e.response?.data?.detail || e.message));
              } finally { setDeptPlanSaving(false); }
            };

            // Отправить / Утвердить
            const handleSubmitPlan = async () => {
              if (!deptPlan?.id) return;
              try {
                await api.submitDeptKPIPlan(deptPlan.id);
                loadData(deptId);
              } catch (e) { alert("Xato: " + (e.response?.data?.detail || e.message)); }
            };
            const handleApprovePlan = async () => {
              if (!deptPlan?.id || !window.confirm("Tasdiqlaysizmi?")) return;
              try {
                await api.approveDeptKPIPlan(deptPlan.id);
                loadData(deptId);
              } catch (e) { alert("Xato: " + (e.response?.data?.detail || e.message)); }
            };

            // Скачать Word
            const handleDownloadPlanWord = async () => {
              if (!deptPlan?.id) return;
              try {
                const r = await api.downloadDeptKPIPlanWord(deptPlan.id);
                const url = URL.createObjectURL(r.data);
                const a = document.createElement('a');
                a.href = url;
                a.download = `KPI_dept_${month}_${year}.docx`;
                a.click();
                URL.revokeObjectURL(url);
              } catch (e) { alert("Xato: " + e.message); }
            };

            // Подтянуть данные из оценок сотрудников
            const handleAutoAggregate = async () => {
              if (!deptPlan?.id) return;
              setDeptPlanSaving(true);
              try {
                const r = await api.autoAggregateDeptKPIPlan(deptPlan.id);
                const d = r.data;
                alert(
                  `Tizimdan ma'lumotlar olindi:\n\n` +
                  `1.1 Xodimlar KPI: ${d.employees_counted} ta xodim, o'rtacha ${d.avg_total_score} ball\n` +
                  `1.3 Topshiriqlar: hujjatlar modulidan\n` +
                  `3.1 Davomat: HR modulidan\n\n` +
                  `Yangilangan: ${d.updated} ta ko'rsatkich\n` +
                  `Qo'lda to'ldiring: 1.2, 2.1, 2.2, 2.3`
                );
                loadData(deptId);
              } catch (e) {
                alert("Xato: " + (e.response?.data?.detail || e.message));
              } finally { setDeptPlanSaving(false); }
            };

            if (!deptPlan) {
              return (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <Building2 size={48} color="#6c757d" style={{ marginBottom: 16 }} />
                  <p style={{ color: '#6c757d', fontSize: 16, marginBottom: 16 }}>
                    Bu oy uchun bo'linma KPI rejasi hali yaratilmagan
                  </p>
                  {(isHead || isAdmin) && (
                    <button onClick={handleCreatePlan} disabled={deptPlanSaving} style={{
                      padding: '10px 24px', borderRadius: 8, border: 'none',
                      background: '#0d6efd', color: '#fff', cursor: 'pointer',
                      fontSize: 15, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 8,
                    }}>
                      <Plus size={16} /> Reja yaratish
                    </button>
                  )}
                </div>
              );
            }

            const isApproved = deptPlan.status === 'approved';
            const totalScore = (deptPlan.criteria_data || []).reduce((s, e) => s + (Number(e.score) || 0), 0);
            const maxScore = deptPlan.total_weight || 80;

            return (
              <div>
                {/* Статус и заголовок */}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  marginBottom: 20, flexWrap: 'wrap', gap: 12,
                }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                      KPI rejasi — {deptPlan.period_display}
                    </h3>
                    <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center' }}>
                      <StatusBadge status={deptPlan.status} />
                      <GradeBadge grade={deptPlan.grade} />
                      <span style={{ fontSize: 14, fontWeight: 700 }}>
                        {Math.round(totalScore * 10) / 10} / {maxScore} ball
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {!isApproved && (isHead || isAdmin) && (
                      <button onClick={handleSavePlan} disabled={deptPlanSaving} style={{
                        padding: '8px 16px', borderRadius: 8, border: 'none',
                        background: '#0d6efd', color: '#fff', cursor: 'pointer', fontSize: 13,
                        fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
                      }}>
                        <Save size={14} /> Saqlash
                      </button>
                    )}
                    {deptPlan.status === 'draft' && (isHead || isAdmin) && (
                      <button onClick={handleSubmitPlan} style={{
                        padding: '8px 16px', borderRadius: 8, border: 'none',
                        background: '#fd7e14', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                      }}>
                        Yuborish
                      </button>
                    )}
                    {deptPlan.status === 'submitted' && isAdmin && (
                      <button onClick={handleApprovePlan} style={{
                        padding: '8px 16px', borderRadius: 8, border: 'none',
                        background: '#198754', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                      }}>
                        <CheckCircle size={14} /> Tasdiqlash
                      </button>
                    )}
                    {!isApproved && (isHead || isAdmin) && (
                      <button onClick={handleAutoAggregate} disabled={deptPlanSaving} style={{
                        padding: '8px 16px', borderRadius: 8, border: '1px solid #17a2b8',
                        background: '#e3f8fc', color: '#0c7c8c', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}>
                        <RefreshCw size={14} /> Xodimlardan olish
                      </button>
                    )}
                    <button onClick={handleDownloadPlanWord} style={{
                      padding: '8px 16px', borderRadius: 8, border: '1px solid #6f42c1',
                      background: '#fff', color: '#6f42c1', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                      <FileText size={14} /> Word
                    </button>
                  </div>
                </div>

                {/* Раис (начальник отдела) */}
                {!isApproved && (isHead || isAdmin) && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: 13, fontWeight: 600 }}>
                      Bo'linma raisi:
                      <input
                        type="text"
                        value={deptPlan.head_name || ''}
                        onChange={e => setDeptPlan(prev => ({ ...prev, head_name: e.target.value }))}
                        placeholder="F.I.Sh."
                        style={{
                          marginLeft: 8, padding: '6px 12px', borderRadius: 6,
                          border: '1px solid #dee2e6', fontSize: 13, width: 250,
                        }}
                      />
                    </label>
                  </div>
                )}

                {/* Таблица критериев */}
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th style={{ ...thStyle, background: '#1F4E79', color: '#fff', width: 50 }}>T/r</th>
                        <th style={{ ...thStyle, background: '#1F4E79', color: '#fff', textAlign: 'left' }}>Ko'rsatkichlar nomi</th>
                        <th style={{ ...thStyle, background: '#1F4E79', color: '#fff', width: 60 }}>Ulushi %</th>
                        <th style={{ ...thStyle, background: '#1F4E79', color: '#fff', width: 80 }}>Reja</th>
                        <th style={{ ...thStyle, background: '#1F4E79', color: '#fff', width: 80 }}>Fakt</th>
                        <th style={{ ...thStyle, background: '#1F4E79', color: '#fff', width: 70 }}>Ijro %</th>
                        <th style={{ ...thStyle, background: '#1F4E79', color: '#fff', width: 70 }}>Ball</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Строка JAMI */}
                      <tr style={{ background: '#FFF9C4', fontWeight: 700 }}>
                        <td style={tdStyle}></td>
                        <td style={{ ...tdStyle, textAlign: 'left', fontWeight: 700 }}>JAMI</td>
                        <td style={{ ...tdStyle, fontWeight: 700 }}>{maxScore}</td>
                        <td style={tdStyle}></td>
                        <td style={tdStyle}></td>
                        <td style={tdStyle}></td>
                        <td style={{ ...tdStyle, fontWeight: 700 }}>{Math.round(totalScore * 10) / 10}</td>
                      </tr>

                      {/* Группы и подкритерии */}
                      {groups.map((grp) => {
                        const grpData = (deptPlan.criteria_data || []).filter(e => e.group_number === grp.group_number);
                        const grpScore = grpData.reduce((s, e) => s + (Number(e.score) || 0), 0);

                        return (
                          <React.Fragment key={grp.group_number}>
                            {/* Строка группы */}
                            <tr style={{ background: '#e3f2fd' }}>
                              <td style={{ ...tdStyle, fontWeight: 700, color: '#1565c0' }}>{grp.group_number}</td>
                              <td style={{ ...tdStyle, textAlign: 'left', fontWeight: 700, color: '#1565c0' }}>
                                {grp.group_name}
                              </td>
                              <td style={{ ...tdStyle, fontWeight: 700, color: '#1565c0' }}>{grp.group_weight}</td>
                              <td style={tdStyle}></td>
                              <td style={tdStyle}></td>
                              <td style={tdStyle}></td>
                              <td style={{ ...tdStyle, fontWeight: 700, color: '#1565c0' }}>
                                {Math.round(grpScore * 10) / 10}
                              </td>
                            </tr>

                            {/* Подкритерии */}
                            {(grp.subcriteria || []).map((sc) => {
                              const ev = grpData.find(e => e.criterion_number === sc.number) || {};
                              return (
                                <tr key={sc.number}>
                                  <td style={tdStyle}>{sc.number}</td>
                                  <td style={{ ...tdStyle, textAlign: 'left', fontSize: 12 }}>{sc.name}</td>
                                  <td style={tdStyle}>{sc.weight}</td>
                                  <td style={tdStyle}>
                                    <input
                                      type="number" min={0}
                                      value={ev.plan_value || ''}
                                      onChange={e => updateDeptCriterion(sc.number, 'plan_value', e.target.value === '' ? 0 : Number(e.target.value))}
                                      disabled={isApproved}
                                      style={{
                                        width: 60, padding: '3px 6px', borderRadius: 4,
                                        border: '1px solid #dee2e6', fontSize: 12, textAlign: 'center',
                                      }}
                                    />
                                  </td>
                                  <td style={tdStyle}>
                                    <input
                                      type="number" min={0}
                                      value={ev.actual_value || ''}
                                      onChange={e => updateDeptCriterion(sc.number, 'actual_value', e.target.value === '' ? 0 : Number(e.target.value))}
                                      disabled={isApproved}
                                      style={{
                                        width: 60, padding: '3px 6px', borderRadius: 4,
                                        border: '1px solid #dee2e6', fontSize: 12, textAlign: 'center',
                                      }}
                                    />
                                  </td>
                                  <td style={{
                                    ...tdStyle, fontWeight: 600,
                                    color: (ev.execution_pct || 0) >= 100 ? '#198754' : (ev.execution_pct || 0) >= 70 ? '#fd7e14' : '#dc3545',
                                  }}>
                                    {(ev.execution_pct || 0) > 0 ? `${ev.execution_pct}%` : ''}
                                  </td>
                                  <td style={{ ...tdStyle, fontWeight: 600 }}>
                                    {(ev.score || 0) > 0 ? (Math.round(ev.score * 10) / 10) : ''}
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Шкала оценок */}
                <div style={{ marginTop: 20, padding: 14, background: '#f8f9fa', borderRadius: 8, border: '1px solid #e9ecef' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Rejaning bajarilishidan kelib chiqib:</div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {(() => {
                      const tw = maxScore || 100;
                      const r = tw / 100;
                      return [
                        { grade: 'A', label: "A'lo", condition: `≥${Math.round(100*r)} ball`, bg: '#C6EFCE' },
                        { grade: 'B', label: 'Yaxshi', condition: `${Math.round(85*r)}–${Math.round(100*r)-1} ball`, bg: '#FFEB9C' },
                        { grade: 'C', label: 'Qoniqarli', condition: `${Math.round(70*r)}–${Math.round(85*r)-1} ball`, bg: '#FFD9B3' },
                        { grade: 'D', label: 'Qoniqarsiz', condition: `<${Math.round(70*r)} ball`, bg: '#FFC7CE' },
                      ];
                    })().map(g => (
                      <div key={g.grade} style={{
                        padding: '6px 14px', borderRadius: 6, fontSize: 12,
                        background: deptPlan.grade === g.grade ? g.bg : '#fff',
                        border: deptPlan.grade === g.grade ? '2px solid #333' : '1px solid #dee2e6',
                        fontWeight: deptPlan.grade === g.grade ? 700 : 400,
                      }}>
                        {g.grade} — {g.label} ({g.condition})
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Вкладка: Шаблон ─── */}
      {activeTab === 'template' && (
        <div>
          {/* Шапка с названием отдела */}
          {(() => {
            const currentDeptId = selectedDept || user.department_id;
            const currentDept = departments.find(d => String(d.id) === String(currentDeptId));
            const deptLabel = currentDept ? (currentDept.name_ru || currentDept.name) : (user.department_name || '');
            return deptLabel ? (
              <div style={{
                background: '#e8f4fd', border: '1px solid #bee3f8', borderRadius: 8,
                padding: '10px 16px', marginBottom: 16, fontSize: 14, color: '#1565c0',
              }}>
                {t('templateTab.header')} <strong>{deptLabel}</strong>
                {isAdmin && (
                  <span style={{ float: 'right', fontSize: 12, color: '#6c757d' }}>
                    {t('templateTab.filterHint')}
                  </span>
                )}
              </div>
            ) : null;
          })()}
          {template ? (
            <div>
              {/* Официальная шапка 2-ilova */}
              {(() => {
                const monthUz = MONTHS_UZ_LIST.find(m => m.v === (template.order_month || 8))?.uz || 'avgust';
                const day    = template.order_day    || '___';
                const num    = template.order_number || '____';
                const year   = template.order_year   || 2025;
                return (
                  <div style={{
                    background: '#fff9e6', border: '1px solid #ffe58f', borderRadius: 8,
                    padding: '12px 18px', marginBottom: 16, textAlign: 'right',
                    fontSize: 13, color: '#7d4800', fontStyle: 'italic', lineHeight: 1.8,
                  }}>
                    "Farmatsevtika mahsulotlari xavfsizligi<br/>
                    markazi" davlat muassasasi direktorining<br/>
                    <strong>{year}</strong>-yil "{day}" <strong>{monthUz}dagi</strong> <strong>{num}</strong> -sonli buyrug'iga<br/>
                    <span style={{ fontStyle: 'normal', fontWeight: 700, fontSize: 14, color: '#495057' }}>2-ilova</span>
                  </div>
                );
              })()}

              {/* Заголовок таблицы (центр страницы в документе) */}
              <div style={{
                background: '#f0f4ff', border: '1px solid #bee3f8', borderRadius: 8,
                padding: '10px 16px', marginBottom: 16, textAlign: 'center',
                fontSize: 13, fontWeight: 700, color: '#1565c0', lineHeight: 1.6,
              }}>
                {template.document_title || (
                  <span>
                    {(() => {
                      const currentDept = departments.find(d => String(d.id) === String(template.department));
                      const dn = currentDept?.name_uz || currentDept?.name_ru || '';
                      return dn
                        ? `${dn} xodimlarining eng muhim samaradorlik ko'rsatkichlari va baholash mezoni`
                        : `Xodimlarning eng muhim samaradorlik ko'rsatkichlari va baholash mezoni`;
                    })()}
                    <span style={{ display: 'block', fontSize: 11, color: '#6c757d', fontWeight: 400, marginTop: 2 }}>
                      ({t('templateTab.autoTitleHint')})
                    </span>
                  </span>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontSize: 17 }}>{t('templateTab.currentTitle')}</h3>
                <button onClick={() => setTemplateModal(true)} style={actionBtnStyle('#0d6efd')}>
                  <Edit2 size={14} /> {t('templateTab.edit')}
                </button>
              </div>
              <div style={{ display: 'grid', gap: 10 }}>
                {(template.criteria || []).map((group, gi) => (
                  <div key={gi} style={{
                    background: '#fff', border: '1px solid #e9ecef', borderRadius: 8, padding: 14,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{
                        background: '#1565c0', color: '#fff', borderRadius: '50%',
                        width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
                      }}>{group.group_number}</span>
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{group.group_name}</span>
                      <span style={{ marginLeft: 'auto', fontSize: 13, color: '#1565c0', fontWeight: 600 }}>
                        {group.group_weight} ball
                      </span>
                    </div>
                    {(group.subcriteria || []).map((sc, si) => (
                      <div key={si} style={{
                        marginLeft: 32, padding: '6px 0', borderBottom: si < (group.subcriteria || []).length - 1 ? '1px solid #f0f0f0' : 'none',
                      }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#1565c0', marginRight: 8 }}>{sc.number}</span>
                        <span style={{ fontSize: 13 }}>{sc.name}</span>
                        <span style={{ marginLeft: 8, fontSize: 12, color: '#6c757d' }}>({sc.weight} ball)</span>
                        {sc.scoring_rule && (
                          <p style={{ margin: '2px 0 0', fontSize: 11, color: '#6c757d' }}>{sc.scoring_rule.substring(0, 150)}{sc.scoring_rule.length > 150 ? '...' : ''}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
                <div style={{ background: '#f0f7ff', border: '1px solid #bee3f8', borderRadius: 8, padding: 14 }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>
                    {t('templateTab.totalMax')} {template.total_max_score} ball
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 60 }}>
              <Settings size={48} color="#dee2e6" style={{ marginBottom: 16 }} />
              <h3 style={{ color: '#6c757d', marginBottom: 8 }}>{t('templateTab.emptyTitle')}</h3>
              <p style={{ color: '#adb5bd', marginBottom: 20 }}>
                {t('templateTab.emptySubtitle')}
              </p>
              {(isHead || isAdmin) && (
                <button onClick={() => setTemplateModal(true)} style={{
                  padding: '10px 24px', borderRadius: 8,
                  background: '#0d6efd', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 14,
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                }}>
                  <Plus size={16} /> {t('templateTab.create')}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Вкладка: Сводные отчёты ─── */}
      {activeTab === 'reports' && (
        <div>
          {/* Статистические карточки */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 24 }}>
            {['A', 'B', 'C', 'D'].map(grade => {
              const gradeRecords = records.filter(r => r.grade === grade);
              const cfg = GRADE_COLORS[grade];
              return (
                <div key={grade} style={{
                  background: cfg.bg, border: `1px solid ${cfg.border}`,
                  borderRadius: 10, padding: 18, textAlign: 'center',
                }}>
                  <div style={{ fontSize: 32, fontWeight: 900, color: cfg.text }}>{grade}</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: cfg.text }}>{gradeRecords.length}</div>
                  <div style={{ fontSize: 12, color: cfg.text, opacity: 0.8 }}>
                    {t(`reportsTab.grade${grade}`)}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Таблица сводки */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr>
                  {[t('table.num'), t('table.employee'), t('reportsTab.position'), t('reportsTab.score'), '%', t('table.grade'), t('table.status'), t('table.actions')].map(h => (
                    <th key={h} style={{
                      ...thStyle, background: '#1F4E79', color: '#fff', border: '1px solid #164080',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...records]
                  .sort((a, b) => b.total_score - a.total_score)
                  .map((rec, idx) => (
                    <tr key={rec.id} style={{ background: idx % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                      <td style={tdStyle}>{idx + 1}</td>
                      <td style={{ ...tdStyle, textAlign: 'left', fontWeight: 600 }}>{rec.employee_name}</td>
                      <td style={{ ...tdStyle, textAlign: 'left', fontSize: 12, color: '#6c757d' }}>{rec.employee_position}</td>
                      <td style={tdStyle}>{rec.total_score} / {rec.max_possible_score}</td>
                      <td style={tdStyle}>{rec.percentage?.toFixed(1)}%</td>
                      <td style={tdStyle}><GradeBadge grade={rec.grade} /></td>
                      <td style={tdStyle}><StatusBadge status={rec.status} /></td>
                      <td style={tdStyle}>
                        <button onClick={() => handleDownloadWord(rec)} style={actionBtnStyle('#6f42c1')}>
                          <FileText size={13} /> 2-ilova
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {records.length > 0 && (
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <button onClick={handleDownloadExcel} style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 20px', borderRadius: 8,
                background: '#198754', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 14,
              }}>
                <Download size={16} /> {t('reportsTab.downloadExcel')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Модалы */}
      {evalModal && (
        <EvaluationModal
          employee={evalModal.employee}
          record={evalModal.record}
          template={template}
          month={month}
          year={year}
          onClose={() => setEvalModal(null)}
          onSave={() => loadData(selectedDept || user.department_id || '')}
        />
      )}
      {templateModal && (
        <TemplateModal
          template={template}
          department={selectedDept || user.department_id}
          onClose={() => setTemplateModal(false)}
          onSave={() => { setTemplateModal(false); loadTemplate(selectedDept || user.department_id || ''); }}
        />
      )}
    </div>
  );
}

// ─── Стили ────────────────────────────────────────────────────────────────────

const thStyle = {
  padding: '10px 12px', textAlign: 'center', fontWeight: 600,
  border: '1px solid #e9ecef', fontSize: 13,
  background: '#f8f9fa',
};

const tdStyle = {
  padding: '10px 12px', textAlign: 'center',
  border: '1px solid #f0f0f0', verticalAlign: 'middle',
};

const actionBtnStyle = (color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '5px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
  background: color + '15', color, border: `1px solid ${color}40`,
  fontWeight: 600, whiteSpace: 'nowrap',
});
